import { config } from '@/config';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

export interface PersonalAccessToken {
  id: string;
  name: string;
  scopes: string[];
  createdAt: string;
  expiresAt: string;
  lastUsedAt?: string;
}

export interface PatListResponse {
  tokens: PersonalAccessToken[];
  total: number;
}

export interface PatCreateRequest {
  name: string;
}

export interface PatCreateResponse {
  id: string;
  token: string;
  name: string;
  scopes: string[];
  createdAt: string;
  expiresAt: string;
}

const waitForAuthReady = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      if (user) {
        resolve();
      } else {
        reject(new Error('User not authenticated'));
      }
    });
  });
};

const getAuthToken = async (): Promise<string> => {
  const auth = getAuth();
  let currentUser = auth.currentUser;

  if (!currentUser) {
    await waitForAuthReady();
    currentUser = auth.currentUser;
  }

  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  return await currentUser.getIdToken();
};

export const listPersonalAccessTokens = async (): Promise<PatListResponse> => {
  const idToken = await getAuthToken();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  let response;
  try {
    response = await fetch(`${config.identity.url}/account/personal-access-token`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw err;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.message || `Failed to fetch tokens: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data;
};

export const createPersonalAccessToken = async (
  payload: PatCreateRequest,
): Promise<PatCreateResponse> => {
  const idToken = await getAuthToken();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  let response;
  try {
    response = await fetch(`${config.identity.url}/account/personal-access-token`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw err;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.message || `Failed to create token: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data;
};

export const deletePersonalAccessToken = async (tokenId: string): Promise<void> => {
  const idToken = await getAuthToken();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  let response;
  try {
    response = await fetch(`${config.identity.url}/account/personal-access-token/${tokenId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw err;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.message || `Failed to delete token: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }
};
